/**
 * The intermediate representation.
 *
 * This is the crux of the whole project: a block declares its structure ONCE as an IR tree,
 * and three consumers derive from it — the React renderer, the Webflow clipboard emitter,
 * and the MCP HTML emitter. Because they read the same tree, the editor preview and the
 * Webflow export cannot drift apart.
 *
 * Two constraints are deliberate and load-bearing:
 *
 *   1. No inline styles, except on `IREmbed`. That one exception exists solely to reproduce
 *      Collctiv's live CTA convention, which really is a raw inline-styled <button> inside a
 *      `w-embed w-script`. We reproduce their convention rather than improve on it, because
 *      their analytics binds to it.
 *
 *   2. No arbitrary CSS. `cls` names must exist in the token registry; the exporter throws
 *      otherwise. That is what makes brand safety structural rather than advisory.
 */

/**
 * Webflow Designer node types, as observed in real `@webflow/XscpData` payloads.
 *
 * This is a subset — the types our blocks actually emit. The full vocabulary is wider
 * (Forms, Navbar, Slider, Tabs, Lightbox), but emitting a type we have never round-tripped
 * against a real Designer is how you ship something that silently breaks on paste.
 *
 * Note these are NOT interchangeable with the Designer API's `elementPresets` names, which
 * are similar but differ (the API says `DivBlock` where the clipboard says `Block`).
 */
export type WebflowNodeType =
  | 'Section'
  | 'Container'
  | 'BlockContainer'
  | 'Block'
  | 'Grid'
  | 'VFlex'
  | 'HFlex'
  | 'Heading'
  | 'Paragraph'
  | 'Link'
  | 'List'
  | 'ListItem'
  | 'Blockquote'
  | 'Strong'
  | 'Emphasized'
  | 'Span'
  | 'RichText'
  | 'Image'
  | 'HtmlEmbed';

/** A reference to a class in the token registry. First entry is the base, rest are combos. */
export interface ClassRef {
  name: string;
  /** True when this is a combo class (`comb: "&"` in XscpData). */
  combo?: boolean;
  /** For a combo, the base class it attaches to. */
  parent?: string;
}

export interface IRElement {
  kind: 'el';
  wf: WebflowNodeType;
  /** Lowercase HTML tag: 'section' | 'div' | 'h2' | 'p' | 'a' | 'ul' | 'li' | 'figure' | ... */
  tag: string;
  cls: ClassRef[];
  /** Native attributes: href, src, alt, width, height, id, loading, fetchpriority, ... */
  attr?: Record<string, string>;
  /** Custom data-* attributes, emitted as XscpData `xattr`. */
  xattr?: Array<{ name: string; value: string }>;
  /** Label shown in the Webflow Designer's navigator, e.g. 'Hero / Copy'. */
  displayName?: string;
  children: IRNode[];
}

export interface IRText {
  kind: 'text';
  value: string;
}

/**
 * A hole filled differently by each consumer: the React renderer hands it to Puck's slot
 * component; the exporters expand it by walking the child blocks and recursing into `toIR`.
 * Exporters must never see an unexpanded slot — `expandSlots` runs first.
 *
 * A slot IS its own container element rather than sitting inside one. That is not a
 * stylistic choice: Puck's drop zone renders a wrapper div of its own, so a grid declared as
 * `el('Grid', …, [slot('items')])` ends up with exactly one child — the wrapper — and every
 * dropped card lands in a single grid cell. Putting the layout classes on the slot means the
 * drop zone *is* the grid, and the exporter builds the equivalent element itself.
 */
export interface IRSlot {
  kind: 'slot';
  name: string;
  /** Classes for the container element that holds the slot's children. */
  cls?: ClassRef[];
  /** Webflow node type for the container the exporter creates. */
  wf?: WebflowNodeType;
  /** Tag for that container. */
  tag?: string;
  displayName?: string;
}

/** A rich-text body. `html` is already sanitised against the allowed-tag whitelist. */
export interface IRRich {
  kind: 'rich';
  html: string;
  cls: ClassRef[];
}

/** Raw HTML passed through verbatim. The only place inline styles are permitted. */
export interface IREmbed {
  kind: 'embed';
  html: string;
  cls: ClassRef[];
}

export type IRNode = IRElement | IRText | IRSlot | IRRich | IREmbed;

/* ------------------------------------------------------------------ *
 * Constructors — terser than object literals and they fix `kind`.
 * ------------------------------------------------------------------ */

export const text = (value: string): IRText => ({ kind: 'text', value });

export const slot = (name: string): IRSlot => ({ kind: 'slot', name });

/**
 * A slot that is also a layout container — the common case.
 *
 * Use this rather than wrapping `slot()` in an element, or the drop zone's own wrapper div
 * becomes the container's only child and the layout collapses to a single cell.
 */
export function slotContainer(
  name: string,
  wf: WebflowNodeType,
  tag: string,
  cls: Array<string | ClassRef>,
  displayName?: string,
): IRSlot {
  return {
    kind: 'slot',
    name,
    wf,
    tag,
    cls: cls.map((c) => (typeof c === 'string' ? { name: c } : c)),
    ...(displayName ? { displayName } : {}),
  };
}

export function el(
  wf: WebflowNodeType,
  tag: string,
  cls: Array<string | ClassRef>,
  children: IRNode[] = [],
  extra: Omit<IRElement, 'kind' | 'wf' | 'tag' | 'cls' | 'children'> = {},
): IRElement {
  return {
    kind: 'el',
    wf,
    tag,
    cls: cls.map((c) => (typeof c === 'string' ? { name: c } : c)),
    children,
    ...extra,
  };
}

/* ------------------------------------------------------------------ *
 * Traversal
 * ------------------------------------------------------------------ */

/** Depth-first walk over every node in a tree. Parents are visited before children. */
export function walkIR(node: IRNode, visit: (n: IRNode) => void): void {
  visit(node);
  if (node.kind === 'el') {
    for (const child of node.children) walkIR(child, visit);
  }
}

/** Every distinct class name referenced anywhere in a tree, in first-seen order. */
export function collectClassNames(nodes: IRNode[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const root of nodes) {
    walkIR(root, (n) => {
      if (n.kind === 'el' || n.kind === 'rich' || n.kind === 'embed') {
        for (const c of n.cls) {
          if (!seen.has(c.name)) {
            seen.add(c.name);
            out.push(c.name);
          }
        }
      }
    });
  }
  return out;
}
